import { Component, OnInit } from '@angular/core';
import { PocketbaseService } from '../../services/pocketbase.service';
import { Course, CourseStats } from '../../interfaces/courseInterface';
import { RouterLink } from '@angular/router';
import { TestService } from '../../services/test.service';
import { DecimalPipe, DatePipe } from '@angular/common';
import { Achievement } from '../../interfaces/achievementInterface';
import { AchievementService } from '../../services/achievement.service';
import { TrainingHistoryEntry } from '../../interfaces/trainingHistoryInterface';
import { NgIcon } from '@ng-icons/core';
import { CourseBackgroundImageService } from '../../services/course-background-image.service';
import { environment } from '../../../environments/environment';


@Component({
    selector: 'app-overview',
    imports: [RouterLink, DecimalPipe, DatePipe, NgIcon],
    templateUrl: './overview.component.html',
    styleUrl: './overview.component.scss'
})
export class OverviewComponent implements OnInit {
  public isNewUser = true; // default: neuer User, bis Gegenteil geprüft

  private readonly DEV_DEMO_COURSE_ID = '0efq5s4371qfnwc';
  private readonly PROD_DEMO_COURSE_ID = 'clgou4yb6w8j89a';

  private readonly DEV_PSM1_COURSE_ID = 'lcv1ndf4mz04xpq';
  private readonly PROD_PSM1_COURSE_ID = 'bxwzmkudjoh2q88';

  private readonly DEV_PAL_COURSE_ID = 'pi9rjuwcuykq4of';
  private readonly PROD_PAL_COURSE_ID = '9l2wrrxe1g1fxlv';

  // Demokurs-ID unterscheidet sich zwischen dev- und prod-Backend,
  // deswegen abhängig vom environment.production-Flag wählen
  private readonly DEMO_COURSE_ID = environment.production
    ? this.PROD_DEMO_COURSE_ID
    : this.DEV_DEMO_COURSE_ID;

  private readonly PSM1_COURSE_ID = environment.production
    ? this.PROD_PSM1_COURSE_ID
    : this.DEV_PSM1_COURSE_ID;

  private readonly PAL_COURSE_ID = environment.production
    ? this.PROD_PAL_COURSE_ID
    : this.DEV_PAL_COURSE_ID;

  // Fallback-Name, falls jemand lokal ein eigenes PocketBase-Backend nutzt, wo die ID nicht mit dev/prod übereinstimmt ._.
  private readonly DEMO_COURSE_NAME = 'Demokurs';
  private readonly PSM1_COURSE_NAME = 'PSM I';
  private readonly PAL_COURSE_NAME = 'PAL';

  // öffentlich fürs Template, defaultet auf die fixe ID, wird bei lokalem Backend ggf. überschrieben
  public psm1CourseId: string = this.PSM1_COURSE_ID;
  public palCourseId: string = this.PAL_COURSE_ID;

  public availableExams: { id: string, name: string }[] = [];
  public lastResults: { date: Date, results: { id: string, title: string, score: number, timeTaken: number, questionAmount: number, startTime: Date }[] }[] = [];
  public achievements: Achievement[] = [];
  public lastTrainings: any;

  public availableCourses: {
  id: string;
  examId: string;
  name: string;
  access: boolean;
  }[] = [];

  constructor(private pocketbaseService: PocketbaseService, private testService: TestService, private achievementService: AchievementService, private courseBackgroundImageService: CourseBackgroundImageService) { }

  async ngOnInit(): Promise<void> {
    console.log(this.DEMO_COURSE_ID);

    await this.checkUserStatus();

    this.fetchLastResults();
    this.fetchAchievements();
    this.fetchLastTrainings();
    if (this.isNewUser) {
    this.fetchAvailableCourses();
  }
  }

  // überprüfen, ob der User neu ist oder nicht über den Demokurs
  async checkUserStatus() {
  const userId = this.pocketbaseService.pb.authStore.model?.['id'];
  if (!userId) return;

  try {
    // Erkennung "lokales Backend" über die baseUrl aus dem environment
    const isLocalBackend = environment.baseUrl.includes('localhost');
    console.log('isLocalBackend:', isLocalBackend);

    // Nur im Local-Fall die vollen Kurs-Objekte per Expand holen (für Namensvergleich),
      // ansonsten reicht das reine ID-Array -> spart Payload
    const user = await this.pocketbaseService.pb.collection('users').getOne(userId, {
      expand: isLocalBackend ? 'courses' : undefined
    });

    // Je nach Backend entweder die expandeten Kurs-Objekte oder die reinen IDs verwenden
    const courses: any[] = user.expand?.['courses'] ?? user['courses'] ?? [];

    // Lokal: über Kursnamen prüfen (ID würde nicht zuverlässig matchen)
    // dev/prod: über die fixe ID prüfen (robuster, IDs ändern sich nicht durch Umbenennung)
    const hasDemoCourse = isLocalBackend
      ? courses.some(c => c.name === this.DEMO_COURSE_NAME)
      : courses.includes(this.DEMO_COURSE_ID);

    // Übergangslösung (siehe Doku): "neu" auch wenn courses leer ist,
    // oder wenn nur der Demokurs drinsteht (max. 1 Eintrag)
    const coursesEmptyOrOnlyDemo = courses.length === 0 ||
      (courses.length === 1 && hasDemoCourse);

    this.isNewUser = hasDemoCourse || coursesEmptyOrOnlyDemo;

    if (isLocalBackend) {
    await this.resolveLocalPsm1CourseId();
    await this.resolveLocalPalCourseId();
    }

    } catch (e) {
      console.error('Fehler beim Prüfen des User-Status', e);
      this.isNewUser = true; // im Zweifel lieber "neu" anzeigen als falsch "zurück"
    }
    console.log('User-Status geprüft. isNewUser:', this.isNewUser);
  }

  private async resolveLocalPsm1CourseId() {
    try {
      const course = await this.pocketbaseService.pb.collection('courses').getFirstListItem<Course>(
        `name = "${this.PSM1_COURSE_NAME}"`
      );
      this.psm1CourseId = course.id;
    } catch (error) {
      console.warn(`Konnte lokalen Kurs "${this.PSM1_COURSE_NAME}" nicht finden`, error);
    }
  }

  private async resolveLocalPalCourseId() {
  try {
    const course = await this.pocketbaseService.pb.collection('courses').getFirstListItem<Course>(
      `name = "${this.PAL_COURSE_NAME}"`
    );
    this.palCourseId = course.id;
  } catch (error) {
    console.warn(`Konnte lokalen Kurs "${this.PAL_COURSE_NAME}" nicht finden`, error);
  }
}

  async fetchLastResults() {
    const results = await this.testService.getUserTestResults(undefined, 1, 5);

    const group: { [key: string]: any[] } = {};

    results.items.forEach(result => {
      const date = new Date(result.test.created).toDateString();
      if (!group[date]) {
        group[date] = [];
      }
      group[date].push({
        id: result.test.id,
        title: result.test.expand.conditions.name,
        score: result.score,
        timeTaken: Math.ceil(result.time_taken) * 1000,
        questionAmount: result.test.expand.conditions.question_amount,
        startTime: new Date(result.test.created)
      })
    })

    this.lastResults = Object.keys(group).map(date => {
      return {
        date: new Date(date),
        results: group[date]
      }
    });
  }

  async fetchAchievements() {
    this.achievements = await this.achievementService.getAchievements();
  }

  async fetchLastTrainings() {
    const result = await this.pocketbaseService.pb.collection("training_history").getList<TrainingHistoryEntry>(1, 5, { expand: 'course' });
    this.lastTrainings = result.items;
  }

  public getCourseCardBackground(courseName: string): string {
    return this.courseBackgroundImageService.getCourseCardBackground(courseName);
  }


  //für new user block
  async fetchAvailableCourses() {
    this.availableCourses = [];

    const courses = await this.pocketbaseService.pb.collection('courses').getFullList<Course>({
      expand: 'exam'
    });

    this.availableCourses = courses
      .filter(course => course.expand?.exam)
      .filter(course => course.access)
      .map(course => ({
        id: course.id,
        examId: course.exam,
        name: course.name,
        access: course.access
      }));
  }
}
